import type {ReactElement} from 'react';
import {useCallback, useContext, useSyncExternalStore} from 'react';
import type {Editor} from 'grapesjs';
import {formMakerContext} from '@FormMakerReact/form-maker.context';
import {areNotificationsEnabled, toggleNotifications} from '@FormMakerReact/lib/form-maker-fields-mutator-library';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2/toggle-switch';
import {Text, TextSize} from '@Components/text/text';
import {IconWithTooltip} from '@Components/icon-with-tooltip/icon-with-tooltip';
import {IconSize} from '@Components/icon-v2/icon.types';
import styles from './new-subscriber-notification-toggle-control.module.scss';

const NOTIFICATION_SYNC_EVENTS = 'component:update:attributes undo redo';

export function NewSubscriberNotificationToggleControl(): ReactElement {
  const {editor} = useContext(formMakerContext);
  const isEnabled = useNotificationsEnabledSync(editor);

  const handleToggle = (): void => {
    if (!editor) {
      return;
    }
    toggleNotifications(editor);
  };

  return (
    <div className={styles.row}>
      <div className={styles.labelWithIcon}>
        <Text val={window.i18next.t('pmwjs_new_subscriber_alerts')} size={TextSize.MD} />
        <IconWithTooltip icon="icon-info" size={IconSize.SIZE_ICON_16} tooltipText={window.i18next.t('pmwjs_new_subscriber_alert_tooltip')} hideArrow />
      </div>
      <ToggleSwitch
        id="form-maker-new-subscriber-notification-toggle"
        isChecked={isEnabled}
        onChange={handleToggle}
        elementTitle={window.i18next.t('pmwjs_new_subscriber_alerts')}
        size={ToggleSwitchV2Size.LARGE}
      />
    </div>
  );
}

function useNotificationsEnabledSync(editor: Editor | null): boolean {
  const subscribe = useCallback(
    (onChange: () => void): (() => void) => {
      if (!editor) {
        return (): void => undefined;
      }
      editor.on(NOTIFICATION_SYNC_EVENTS, onChange);
      return (): void => {
        editor.off(NOTIFICATION_SYNC_EVENTS, onChange);
      };
    },
    [editor]
  );

  const getSnapshot = useCallback((): boolean => {
    return editor ? areNotificationsEnabled(editor) : false;
  }, [editor]);

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
